import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../../components/Footer';
import { ArrowLeft, CheckCircle, MapPin, Phone } from 'lucide-react';

const locations = [
  'Scarsdale', 'Greenwich', 'Rye', 'Harrison', 'Mamaroneck', 'Bedford',
  'Chappaqua', 'Bronxville', 'Larchmont', 'Purchase'
];

const features = [
  {
    title: 'Custom Luxury Vanities',
    description: 'Handcrafted vanities tailored to your space and style preferences, featuring premium materials and expert craftsmanship.'
  },
  {
    title: 'Imported Stone & Marble',
    description: 'Curated selection of rare marble, quartzite, and natural stone from exclusive international quarries.'
  },
  {
    title: 'Spa-Inspired Designs',
    description: 'Transform your bathroom into a personal spa with rainfall showers, freestanding tubs, and steam room features.'
  },
  {
    title: 'Smart Technology',
    description: 'Integration of smart mirrors, digital shower controls, and automated lighting systems.'
  },
  {
    title: 'Heated Flooring',
    description: 'Luxurious radiant floor heating systems for year-round comfort.'
  },
  {
    title: 'Custom Lighting',
    description: 'Layered lighting design with premium fixtures and automated controls.'
  }
];

const portfolioImages = [
  {
    url: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?auto=format&fit=crop&q=80&w=1200',
    location: 'Scarsdale Master Bath'
  },
  {
    url: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&q=80&w=1200',
    location: 'Greenwich Spa Bathroom'
  },
  {
    url: 'https://images.unsplash.com/photo-1604014237800-1c9102c219da?auto=format&fit=crop&q=80&w=1200',
    location: 'Rye Guest Bath'
  }
];

export function BathroomRemodelingPage() {
  return (
    <div className="min-h-screen bg-zinc-900">
      <Helmet>
        <title>Luxury Bathroom Remodeling in Westchester County | Golden Touch</title>
        <meta name="description" content="Premium bathroom remodeling services in Westchester County & Greenwich. Specializing in luxury renovations with custom vanities, imported marble, and spa-inspired designs." />
        <meta name="keywords" content="bathroom remodeling Westchester, luxury bathroom renovation Greenwich, custom bathroom design Scarsdale, spa bathroom Rye" />
        <link rel="canonical" href="https://renovationwestchester.com/services/bathroom-remodeling-westchester" />
      </Helmet>


      <main className="pt-24">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Link to="/services" className="inline-flex items-center gap-2 text-yellow-400 mb-12 hover:gap-4 transition-all duration-300">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm tracking-wider">Back to Services</span>
          </Link>

          <div className="grid md:grid-cols-2 gap-16 items-center mb-24">
            <div>
              <h1 className="text-4xl md:text-6xl font-light text-zinc-100 mb-6">
                Luxury Bathroom Remodeling in Westchester
              </h1>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Transform your bathroom into a luxurious sanctuary with our premium remodeling services. 
                Serving distinguished homes throughout Westchester County and Lower Connecticut with 
                unparalleled craftsmanship and attention to detail.
              </p>
              <div className="flex items-center gap-2 text-yellow-400 mb-8">
                <MapPin className="h-5 w-5" />
                <span className="text-sm tracking-wider">Westchester County & Greenwich Area</span>
              </div>
              <Link to="/consultation" className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900">
                <Phone className="h-4 w-4" />
                <span className="text-sm tracking-wider">Schedule a Consultation</span>
              </Link>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src={portfolioImages[0].url}
                alt="Luxury Bathroom Remodel in Westchester"
                className="w-full h-full object-cover"
                width="1200"
                height="900"
              />
            </div>
          </div>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Premium Features</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-zinc-800/30 p-8">
                  <CheckCircle className="h-8 w-8 text-yellow-400 mb-4" />
                  <h3 className="text-xl text-zinc-100 mb-4">{feature.title}</h3>
                  <p className="text-zinc-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Areas Served</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {locations.map((location) => (
                <Link
                  key={location}
                  to={`/areas/${location.toLowerCase()}`}
                  className="block bg-zinc-800/30 p-4 text-center border border-transparent hover:border-yellow-400/40 transition-colors"
                >
                  <MapPin className="h-5 w-5 text-yellow-400 mx-auto mb-2" />
                  <span className="text-zinc-300">{location}</span>
                </Link>
              ))}
            </div>
          </section>

          <section className="text-center">
            <h2 className="text-3xl font-light text-zinc-100 mb-8">Ready to Transform Your Bathroom?</h2>
            <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
              Let's discuss your vision for a luxurious, spa-inspired bathroom. Our expert team is ready 
              to bring your dreams to life with unparalleled craftsmanship and attention to detail.
            </p>
            <Link to="/consultation" className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900">
              <Phone className="h-4 w-4" />
              <span className="text-sm tracking-wider">Schedule Your Consultation</span>
            </Link>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}